import PhoneInput from '@/features/homePage/components/contactUs/PhoneInput';
import FormInput from '@/features/homePage/components/ui/FormInput';
import FormSelect from '@/components/ui/FormSelect';
import { endpoints } from '@/lib/axios/axios-global';
import { apiGet, apiPost } from '@/lib/axios/axios-utils';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import Box from '@/assets/svg/Box';
import { useSearchParams } from 'react-router-dom';

interface ProductFormProps {
    isOpen: boolean;
    onClose: () => void;
}

const ProductForm = ({ isOpen, onClose }: ProductFormProps) => {
    const [searchParams] = useSearchParams();
    const social = searchParams.get('social');

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [name, setName] = useState('');
    const [company_name, setCompany_name] = useState('');
    const [phone, setPhone] = useState('');
    const [notes, setNotes] = useState('');
    const [email, setEmail] = useState('');
    const [product_id, setProduct_id] = useState<string[]>([]);
    const [productOptions, setProductOptions] = useState<{ value: string; label: string }[]>([]);
    const [fieldErrors, setFieldErrors] = useState<{
        name?: string;
        email?: string;
        company_name?: string;
        product_id?: string;
        phone?: string;
        notes?: string;
    }>({});

    const { t } = useTranslation();

    // Function to reset form fields
    const resetForm = () => {
        setName('');
        setProduct_id([]);
        setCompany_name('');
        setPhone('');
        setNotes('');
        setEmail('');
        setFieldErrors({});
        setError(null);
    };

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setFieldErrors({});

        try {
            await apiPost({
                endpoint: social ? `${endpoints.product.post}?social=${social}` : endpoints.product.post,
                body: {
                    name,
                    product_id: product_id,
                    company_name,
                    phone,
                    email,
                    notes,
                },
                config: { headers: { 'Content-Type': 'multipart/form-data' } },
            });
            toast.success(t('تم إرسال النموذج بنجاح!'), {
                position: 'top-right',
                duration: 3000,
            });
            resetForm();
            onClose();
        } catch (error: any) {
            if (error.response?.data?.errors) {
                const newFieldErrors: typeof fieldErrors = {};
                Object.entries(error.response.data.errors).forEach(([field, messages]) => {
                    if (Array.isArray(messages) && messages.length > 0) {
                        newFieldErrors[field as keyof typeof fieldErrors] = messages[0] as string;
                    }
                });
                setFieldErrors(newFieldErrors);
                toast.error('يرجى تصحيح الأخطاء في النموذج', {
                    duration: 4000,
                    position: 'top-right',
                });
            } else {
                let errorMessage = 'حدث خطأ أثناء إرسال البيانات. يرجى المحاولة مرة أخرى.';
                if (error.response) {
                    errorMessage = error.response.data.msg || errorMessage;
                } else if (error.request) {
                    errorMessage = 'تعذر الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت.';
                }
                setError(errorMessage);
                toast.error(errorMessage, {
                    duration: 4000,
                    position: 'top-right',
                });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchProducts = async () => {
            const response = await apiGet({
                endpoint: endpoints.product.get,
            });

            if (response.data.success) {
                const productOptions = response.data.data.map((product: any) => ({
                    value: product.id.toString(),
                    label: product.name_ar,
                }));
                setProductOptions(productOptions);
            } else {
                toast.error(response.data.msg, {
                    duration: 4000,
                    position: 'top-right',
                });
            }
        };
        fetchProducts();
    }, []);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-3">
            <div className="bg-[#F4F8FB] max-w-[645px] w-full rounded-[16px] lg:p-[32px] md:p-[27px] sm:p-[22px] p-[16px] relative max-h-[90vh] overflow-y-auto">
                <button
                    onClick={onClose}
                    className="absolute md:top-8 top-4 left-7 text-[#7F7F7F] hover:text-[#1e63a3] focus:outline-none bg-gray-300 p-2 rounded-lg"
                >
                    <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                <div className="flex gap-1">
                    <Box className="md:w-[30px] w-[25px] md:h-[30px] h-[25px] md:mt-0 mt-[2px]" />
                    <h2 className="text-[#082743] md:text-[20px] text-[18px] font-bold mb-4 text-right font-cairo">
                        {t('طلب منتج')}
                    </h2>
                </div>
                <div className="block h-[1px] w-full bg-[#D1D1D1] mb-4"></div>

                <form onSubmit={onSubmit} className="text-right grid sm:grid-cols-2 grid-cols-1 gap-x-6 gap-y-4">
                    <FormInput
                        className2="!text-[#082743]"
                        type="text"
                        name="name"
                        placeholder="أدخل اسمك"
                        label="الاسم"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        error={fieldErrors.name}
                    />
                    <FormInput
                        className2="!text-[#082743]"
                        type="text"
                        name="company_name"
                        placeholder="ادخل اسم الشركة"
                        label="اسم الشركة"
                        value={company_name}
                        onChange={(e) => setCompany_name(e.target.value)}
                        error={fieldErrors.company_name}
                    />
                    <div className="sm:col-span-2">
                        <FormInput
                            className2="!text-[#082743]"
                            type="email"
                            name="email"
                            placeholder="example@email.com"
                            label="البريد الإلكتروني"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            error={fieldErrors.email}
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <PhoneInput
                            type="text"
                            name="phone"
                            placeholder="05xxxxxxx"
                            label="رقم الهاتف"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            error={fieldErrors.phone}
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <FormSelect
                            name="product_id"
                            label="اختر المنتجات التي تريدها"
                            options={productOptions}
                            value={product_id}
                            onChange={(e) => {
                                const checkboxValue = e.target.value;
                                if (e.target.checked) {
                                    setProduct_id((prev) => [...prev, checkboxValue]);
                                } else {
                                    setProduct_id((prev) => prev.filter((id) => id !== checkboxValue));
                                }
                            }}
                            error={fieldErrors.product_id}
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 bg-primary-button text-white-blue-50 rounded-lg font-cairo font-bold hover:bg-[#1e63a3] transition"
                        >
                            {loading ? 'جاري إرسال الطلب...' : 'إرسـال الطلب'}
                        </button>
                    </div>
                    {error && <div className="sm:col-span-2 text-red-500 text-center">{error}</div>}
                </form>
            </div>
        </div>
    );
};

export default ProductForm;
